import _ from "lodash";
import client from "lib/apolloClient/client";
import { generateRandomId } from "Utils";
import {
  writeChecklistElementInCache,
  getOrganizationChecklistsFromCache,
  writeOrganizationChecklistInCache,
} from "./ChecklistGraphqlHelpers";
import { getOrganizationChecklistQuery } from "./ChecklistQuery";
import {
  getAssessmentToolNodeDetailsFromCacheV2,
  writeAssessmentToolNodeDetailsInCache,
  getStaffBasicDetailsFromCache,
} from "modules/CommonGraphqlHelpers";
import {
  createChecklistOptionItemMutation,
  createChecklistItemMutation,
  deleteChecklistOptionMutation,
  deleteChecklistItemMutation,
  updateChecklistItemMutation,
  updateChecklistOptionMutation,
  createChecklistMutation,
  deleteChecklistMutation,
  bulkCreateChecklistItemsMutation,
} from "./ChecklistMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { writeAssessmentToolOfParentInCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import { getUpdatedAssessmentToolDetails } from "modules/AssessmentTool/AssessmentToolModule";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { insertElementInAssessmentTool } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";

export const saveChecklist = ({
  copyToolId,
  mode = "CREATE",
  isGlobal = true,
  title = "",
  parentId,
  parentType,
  filters,
  curriculumProgramId,
  extraParams = {},
  portalType,
  shouldRefetchQuery = false,
} = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const checklistId = copyToolId;
    const copyChecklist = mode == "COPY";
    const userInfo = getUserInfo({ portalType });
    const organizationId = userInfo.org_id;
    const academicYearId = getSelectedAcademicYearId(state);

    const organizationDeatils = getOrganizationChecklistsFromCache({
      id: organizationId,
      filters,
    });

    const checklists = _.get(organizationDeatils, "checklists", []) || [];
    let checklist = getAssessmentToolNodeDetailsFromCacheV2({
      id: checklistId,
      type: "CHECKLIST",
    });
    const checklistItems = _.get(extraParams, "checklistItems") || [
      { label: "" },
    ];

    const checklistOptionItems = _.get(extraParams, "checklistOptionItems") || [
      { label: "Yes" },
      { label: "No" },
    ];

    if (!copyChecklist) {
      checklist = {
        id: generateRandomId(),
        __typename: "Checklist",
        title: "",
        checklistItems: _.map(checklistItems, checklist => {
          return {
            ...checklist,
            id: generateRandomId(),
            __typename: "ChecklistItem",
          };
        }),
        checklistOptionItems: _.map(checklistOptionItems, checklist => {
          return {
            ...checklist,
            id: generateRandomId(),
            __typename: "ChecklistOptionItem",
          };
        }),
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
      };
    } else {
      checklist = {
        ...checklist,
        id: generateRandomId(),
        title,
        createdBy: getStaffBasicDetailsFromCache(userInfo.id),
      };
    }

    // If true, default options will be used in BE. No need to send from FE (except for AI)
    const useDefaultOptions = _.isEmpty(
      _.get(extraParams, "checklistOptionItems", [])
    );

    const refetchQueries = [];
    if (shouldRefetchQuery) {
      refetchQueries.push({
        query: getOrganizationChecklistQuery,
        variables: {
          id: organizationId,
          filters,
        },
      });
    }

    try {
      const result = await client.mutate({
        mutation: createChecklistMutation,
        variables: {
          input: {
            title,
            copyChecklist,
            checklistId,
            isGlobal,
            createdBy: userInfo.id,
            checklistItems: checklistItems,
            checklistOptionItems: useDefaultOptions
              ? undefined
              : checklistOptionItems,
            parentId,
            parentType,
            curriculumProgramId:
              isGlobal || useDefaultOptions
                ? curriculumProgramId || getSelectedCurriculumId(getState())
                : undefined,
          },
          portalType,
        },
        awaitRefetchQueries: shouldRefetchQuery,
        refetchQueries,
        // optimisticResponse: {
        //   __typename: "Mutation",
        //   planner: {
        //     __typename: "PlannerMutations",
        //     createChecklist: checklist
        //   }
        // },
        update: (
          cache,
          {
            data: {
              planner: { createChecklist: createChecklistMutationResponse },
            },
          }
        ) => {
          if (!_.isEmpty(createChecklistMutationResponse)) {
            if (isGlobal && !shouldRefetchQuery) {
              // isGlobal is true when you save tool as template
              const data = {
                ...organizationDeatils,
                checklists: [...checklists, createChecklistMutationResponse],
              };

              setTimeout(() =>
                writeOrganizationChecklistInCache({
                  id: organizationId,
                  data,
                  filters,
                })
              );
            }

            if (parentId) {
              const assessmentToolDetails = dispatch(
                getUpdatedAssessmentToolDetails({
                  parentId,
                  parentType,
                  mutationResponse: createChecklistMutationResponse,
                  assessmentToolType: "CHECKLIST",
                  extraParams,
                })
              );

              setTimeout(() =>
                writeAssessmentToolOfParentInCache({
                  id: parentId,
                  data: assessmentToolDetails,
                  type: parentType,
                  academicYearId,
                })
              );
            }
          }
        },
      });

      if (mode === "COPY" && isGlobal) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:label_successfully_duplicated",
            locale_params: [
              { key: "label", value: "common:checklist", isPlainText: false },
            ],
            type: "success",
          })
        );
      }
      return _.get(result, "data.planner.createChecklist.id", null);
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      return null;
    }
  };
};

export const createChecklistItem = ({
  label = "",
  checklistId,
  portalType,
  putAfterItemId,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    const checkList = getAssessmentToolNodeDetailsFromCacheV2({
      id: checklistId,
      type: "CHECKLIST",
    });

    try {
      const checklistItems = _.get(checkList, "checklistItems", []);
      if (_.isUndefined(putAfterItemId)) {
        // to handle old flow where putAfterItemId does not exist
        putAfterItemId = _.get(_.last(checklistItems), "id", null);
      }
      const putAfterItemIndex = _.findIndex(checklistItems, {
        id: putAfterItemId,
      });
      const newChecklistItem = {
        __typename: "ChecklistItem",
        id: generateRandomId(),
        label,
        displaySequence: putAfterItemIndex + 1,
      };

      await client.mutate({
        mutation: createChecklistItemMutation,
        variables: {
          checklistId,
          ..._.omit(newChecklistItem, ["id", "__typename"]),
          createdBy: userInfo.id,
          portalType,
          putAfterItemId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            createChecklistItem: newChecklistItem,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createChecklistItem },
            },
          }
        ) => {
          let checklistItems = _.get(checkList, "checklistItems", []);
          checklistItems = insertElementInAssessmentTool({
            putAfterItemId,
            assessmentTool: checklistItems,
            elementToAdd: createChecklistItem,
          });
          const data = {
            ...checkList,
            checklistItems,
          };

          setTimeout(() =>
            writeAssessmentToolNodeDetailsInCache({
              id: checklistId,
              data,
              type: "CHECKLIST",
            })
          );
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const createChecklistOptionItem = ({
  label = "",
  checklistId,
  portalType,
  putAfterItemId,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    const checkList = getAssessmentToolNodeDetailsFromCacheV2({
      id: checklistId,
      type: "CHECKLIST",
    });
    try {
      const checklistOptionItems = _.get(checkList, "checklistOptionItems", []);
      if (_.isUndefined(putAfterItemId)) {
        // to handle old flow where putAfterItemId does not exist
        putAfterItemId = _.get(_.last(checklistOptionItems), "id", null);
      }
      const putAfterItemIndex = _.findIndex(checklistOptionItems, {
        id: putAfterItemId,
      });

      const newChecklistOptionItem = {
        __typename: "ChecklistOptionItem",
        id: generateRandomId(),
        label,
        displaySequence: putAfterItemIndex + 1,
      };

      await client.mutate({
        mutation: createChecklistOptionItemMutation,
        variables: {
          checklistId,
          ..._.omit(newChecklistOptionItem, ["id", "__typename"]),
          createdBy: userInfo.id,
          portalType,
          putAfterItemId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            createChecklistOptionItem: newChecklistOptionItem,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { createChecklistOptionItem },
            },
          }
        ) => {
          let checklistOptionItems = _.get(
            checkList,
            "checklistOptionItems",
            []
          );
          checklistOptionItems = insertElementInAssessmentTool({
            putAfterItemId,
            assessmentTool: checklistOptionItems,
            elementToAdd: createChecklistOptionItem,
          });
          const data = {
            ...checkList,
            checklistOptionItems,
          };
          setTimeout(() =>
            writeAssessmentToolNodeDetailsInCache({
              id: checklistId,
              data,
              type: "CHECKLIST",
            })
          );
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteChecklist = ({ id, filters, showDiscardToast = false }) => {
  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;

    const organizationDeatils = getOrganizationChecklistsFromCache({
      id: organizationId,
      filters,
    });
    const checklists = _.get(organizationDeatils, "checklists", []);
    try {
      await client.mutate({
        mutation: deleteChecklistMutation,
        variables: {
          id: id,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteChecklist: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteChecklist },
            },
          }
        ) => {
          if (deleteChecklist) {
            const updatedChecklists = _.filter(
              checklists,
              checklist => checklist.id != id
            );
            const data = {
              ...organizationDeatils,
              checklists: updatedChecklists,
            };

            setTimeout(() =>
              writeOrganizationChecklistInCache({
                id: organizationId,
                data,
                filters,
              })
            );
          }
        },
      });
      dispatch(
        setToastMsg({
          msg: showDiscardToast
            ? "toastMsgs:label_successfully_discarded"
            : "toastMsgs:label_successfully_deleted",
          type: "success",
          locale_params: [
            { key: "label", value: "common:checklist", isPlainText: false },
          ],
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      setTimeout(() =>
        writeOrganizationChecklistInCache({
          id: organizationId,
          data: organizationDeatils,
          filters,
        })
      );
      console.error(e);
    }
  };
};

export const deleteChecklistOption = ({
  checklistId,
  optionId,
  portalType,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    try {
      await client.mutate({
        mutation: deleteChecklistOptionMutation,
        variables: {
          id: optionId,
          deletedBy: userInfo.id,
          checklistId,
          portalType,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteChecklistOption: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteChecklistOption },
            },
          }
        ) => {
          if (deleteChecklistOption) {
            let checkList = getAssessmentToolNodeDetailsFromCacheV2({
              id: checklistId,
              type: "CHECKLIST",
            });

            let checklistOptionItems = _.get(
              checkList,
              "checklistOptionItems",
              []
            );
            checklistOptionItems = _.filter(
              checklistOptionItems,
              item => item.id != optionId
            );
            checkList = {
              ...checkList,
              checklistOptionItems,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: checklistId,
                data: checkList,
                type: "CHECKLIST",
              })
            );
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteChecklistItem = ({ checklistId, itemId, portalType }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      await client.mutate({
        mutation: deleteChecklistItemMutation,
        variables: {
          id: itemId,
          deletedBy: userInfo.id,
          checklistId,
          portalType,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteChecklistItem: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteChecklistItem },
            },
          }
        ) => {
          if (deleteChecklistItem) {
            let checkList = getAssessmentToolNodeDetailsFromCacheV2({
              id: checklistId,
              type: "CHECKLIST",
            });
            let checklistItems = _.get(checkList, "checklistItems", []);
            checklistItems = _.filter(
              checklistItems,
              item => item.id != itemId
            );
            checkList = {
              ...checkList,
              checklistItems,
            };

            setTimeout(() =>
              writeAssessmentToolNodeDetailsInCache({
                id: checklistId,
                data: checkList,
                type: "CHECKLIST",
              })
            );
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const updateChecklistElement = ({ id, label, type, portalType }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType });
    try {
      let mutation = "";
      switch (type) {
        case "CHECKLIST_ITEM":
          mutation = updateChecklistItemMutation;
          break;
        case "CHECKLIST_OPTION":
          mutation = updateChecklistOptionMutation;
      }

      await client.mutate({
        mutation,
        variables: {
          label,
          updatedBy: userInfo.id,
          id,
          portalType,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const writeChecklistElementLocal = ({
  id,
  type,
  label,
  elementDetails,
}) => {
  return async (dispatch, getState) => {
    const updatedElementDetails = { ...elementDetails, label };

    writeChecklistElementInCache({ id, type, data: updatedElementDetails });
  };
};

export const bulkCreateChecklistItems = params => {
  return async (dispatch, getState) => {
    const { items = [] } = params;
    const userInfo = getUserInfo();

    const input = _.map(items, item => {
      return {
        ...item,
        createdBy: userInfo.id,
      };
    });

    try {
      await client.mutate({
        mutation: bulkCreateChecklistItemsMutation,
        variables: {
          input,
        },
        update: (
          cache,
          {
            data: {
              planner: { bulkCreateChecklistItems },
            },
          }
        ) => {
          const checklistId = _.get(_.first(items), "checklistId");

          const response = _.get(bulkCreateChecklistItems, "response", []);

          if (!_.isEmpty(response)) {
            const checkList = getAssessmentToolNodeDetailsFromCacheV2({
              id: checklistId,
              type: "CHECKLIST",
            });

            const checklistItems = _.get(checkList, "checklistItems", []);

            const updatedChecklistItems = [...checklistItems, ...response];

            const updatedChecklist = {
              ...checkList,
              checklistItems: updatedChecklistItems,
            };

            writeAssessmentToolNodeDetailsInCache({
              id: checklistId,
              data: updatedChecklist,
              type: "CHECKLIST",
            });
          }
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
};

const REDUCER_HANDLERS = {};

const initialState = {
  rubricData: {
    id: null,
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
